import React, { useState } from 'react';
import styled from 'astroturf/react';

import ExpandLessIcon from 'js/design_system/icons/expand_less.svg';

type Section = {
  title: string;
  contents: React.ReactNode;
};

type Props = {
  sections: Section[];
};

const SectionWrapper = styled.div`
  padding-bottom: 64px;

  &:not(:last-child) {
    border-bottom: 1px solid var(--stroke-dark, #141413);
  }
`;

const SectionTitle = styled.button`
  appearance: none;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    color: var(--hover, #4960d1);
    text-decoration: underline;
  }
`;

const ExpandIcon = styled.div<{ open: boolean }>`
  transform: rotate(180deg);
  color: var(--text-primary, #070a15);

  &.open {
    transform: rotate(0deg);
  }
`;

const SectionTitleText = styled.div`
  @import 'stylesheets/design_system/font.scss';

  @include sans-serif__large;
  text-align: start;
`;

const SectionContents = styled.div`
  @import 'stylesheets/design_system/font.scss';

  @include body__large;
  padding-top: 32px;
`;

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 40px;
`;

const SingleSection = ({
  section,
  open,
  onToggle,
}: {
  section: Section;
  open: boolean;
  onToggle: () => void;
}) => {
  return (
    <SectionWrapper>
      <SectionTitle onClick={onToggle}>
        <SectionTitleText>{section.title}</SectionTitleText>
        <ExpandIcon open={open}>
          <ExpandLessIcon />
        </ExpandIcon>
      </SectionTitle>
      {open && <SectionContents>{section.contents}</SectionContents>}
    </SectionWrapper>
  );
};

const Accordion: React.FC<Props> = ({ sections }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  function toggleIndex(i: number) {
    if (openIndex === i) {
      setOpenIndex(null);
    } else {
      setOpenIndex(i);
    }
  }

  return (
    <SectionsWrapper>
      {sections.map((section, i) => (
        <SingleSection
          key={section.title}
          open={openIndex === i}
          onToggle={() => toggleIndex(i)}
          section={section}
        />
      ))}
    </SectionsWrapper>
  );
};

export default Accordion;
